// components/auth/RegisterForm.js - Updated with Theme Context
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building,
  Phone,
  FileText,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Clock,
  Briefcase,
  MessageSquare,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Send,
  RefreshCw,
  XCircle,
  CreditCard,
  Crown,
  Star,
  Zap
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.png';
import { SiteStyles } from '../site/SiteChrome';

// Email Verification Component
const EmailVerificationField = ({ 
  email, 
  onChange, 
  error, 
  loading, 
  disabled,
  isGoogleAuth,
  onVerificationComplete,
  darkMode 
}) => {
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Check if email was already verified on component mount or email change
  useEffect(() => {
    const checkPreviousVerification = async () => {
      if (!email || !validateEmailFormat(email)) return;
      
      // Don't check if already verified in this session
      if (verificationStatus === 'verified') return;
      
      try {
        const response = await fetch('/api/verify/check-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        
        const data = await response.json();
        
        if (data.verified) {
          setVerificationDetails({
            message: 'This email was previously verified. You can verify again if needed.',
            previouslyVerified: true,
            verifiedAt: data.verified_at
          });
        }
      } catch (error) {
        console.error('Error checking previous verification:', error);
      }
    };
    
    const timeoutId = setTimeout(checkPreviousVerification, 500);
    return () => clearTimeout(timeoutId);
  }, [email, verificationStatus]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const validateEmailFormat = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const checkCommonTypos = (email) => {
    const commonMistakes = {
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'outlok.com': 'outlook.com',
      'hotmial.com': 'hotmail.com',
      'hotmai.com': 'hotmail.com'
    };
    
    const domain = email.split('@')[1];
    return commonMistakes[domain] || null;
  };

  const handleSendVerification = async () => {
    if (verificationStatus === 'verified') {
      return;
    }

    if (showCodeInput && verificationStatus === 'pending') {
      if (verificationCode && verificationCode.length === 6) {
        await handleVerifyCode();
        return;
      } else {
        setVerificationDetails(prev => ({
          ...prev,
          codeError: 'Please enter the 6-digit code sent to your email'
        }));
        return;
      }
    }

    if (!email || !validateEmailFormat(email)) {
      setVerificationStatus('error');
      setVerificationDetails({ message: 'Please enter a valid email address' });
      return;
    }

    const typoSuggestion = checkCommonTypos(email);
    if (typoSuggestion) {
      setVerificationStatus('warning');
      setVerificationDetails({ 
        message: `Did you mean ${email.split('@')[0]}@${typoSuggestion}?`,
        suggestion: typoSuggestion
      });
      return;
    }

    setVerifying(true);
    setVerificationStatus(null);

    try {
      const quickCheckResponse = await fetch('/api/verify/email-quick', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const quickCheck = await quickCheckResponse.json();

      if (!quickCheck.valid) {
        setVerificationStatus('error');
        setVerificationDetails({
          message: quickCheck.reason || 'Email domain not found or invalid',
          details: quickCheck
        });
        setVerifying(false);
        return;
      }

      const response = await fetch('/api/verify/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        // Proxy/worker timeout returns an HTML error page, not JSON.
        throw new Error(`Non-JSON ${response.status} from send-code`);
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setVerificationStatus('pending');
        setVerificationDetails({
          message: 'Verification code sent! Check your email.',
          expiresIn: data.expiresIn || 600
        });
        setShowCodeInput(true);
        setResendTimer(60);
      } else {
        setVerificationStatus('error');
        setVerificationDetails({
          message: data.error || 'Failed to send verification code',
          canRetry: true
        });
      }
    } catch (error) {
      setVerificationStatus('error');
      setVerificationDetails({
        message: 'Verification service unavailable. Please try again.',
        canRetry: true
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setVerificationDetails(prev => ({
        ...prev,
        codeError: 'Please enter the 6-digit code'
      }));
      return;
    }

    setVerifying(true);

    try {
      const response = await fetch('/api/verify/confirm-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          code: verificationCode 
        })
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        setVerificationStatus('verified');
        setVerificationDetails({
          message: 'Email verified successfully!',
          verifiedAt: new Date().toISOString()
        });
        setShowCodeInput(false);
        
        if (onVerificationComplete) {
          onVerificationComplete(true, email);
        }
      } else {
        setVerificationDetails(prev => ({
          ...prev,
          codeError: data.error || 'Invalid verification code'
        }));
      }
    } catch (error) {
      setVerificationDetails(prev => ({
        ...prev,
        codeError: 'Failed to verify code. Please try again.'
      }));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    setVerificationCode('');
    setVerificationDetails(prev => ({ ...prev, codeError: null }));
    await handleSendVerification();
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verified':
        return <ShieldCheck className="w-5 h-5 text-green-500" />;
      case 'error':
        return <ShieldAlert className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Shield className="w-5 h-5 text-blue-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Shield className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Mail className="w-5 h-5" />
          </div>
          <input
            type="email"
            name="email"
            value={email || ''}
            onChange={onChange}
            placeholder="Enter your email address"
            disabled={loading || disabled || isGoogleAuth}
            className={`w-full px-4 py-3 pl-12 pr-32 border rounded-lg transition-colors duration-200 ${
              error
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
                : verificationStatus === 'verified'
                ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200'
                : darkMode
                ? 'border-[#28445F] bg-[#13283C] text-white focus:border-blue-500 focus:ring-blue-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            } focus:outline-none focus:ring-2`}
          />
          
          <div className="absolute inset-y-0 right-28 flex items-center">
            {getStatusIcon()}
          </div>

          {!isGoogleAuth && (
            <button
              type="button"
              onClick={showCodeInput && verificationStatus === 'pending' ? handleVerifyCode : handleSendVerification}
              disabled={!email || verifying || verificationStatus === 'verified' || loading}
              className={`absolute inset-y-0 right-0 px-3 m-1 rounded-md font-medium text-sm transition-all ${
                verificationStatus === 'verified'
                  ? 'bg-green-500 text-white cursor-not-allowed'
                  : !email || verifying
                  ? 'bg-[#1C3348] text-slate-500 cursor-not-allowed'
                  : showCodeInput && verificationStatus === 'pending'
                  ? 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
                  : 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
              }`}
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : verificationStatus === 'verified' ? (
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Verified
                </span>
              ) : showCodeInput && verificationStatus === 'pending' ? (
                <span className="flex items-center">
                  Send
                </span>
              ) : (
                <span className="flex items-center">
                  <Send className="w-4 h-4 mr-1" />
                  Verify
                </span>
              )}
            </button>
          )}
        </div>

        {error && !verificationStatus && (
          <p className="text-red-600 text-sm mt-1 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {error}
          </p>
        )}

        {isGoogleAuth && (
          <p className="text-green-600 text-sm mt-1 flex items-center">
            <ShieldCheck className="w-4 h-4 mr-1" />
            Email verified via Google
          </p>
        )}
      </div>

      {verificationStatus && verificationDetails && !showCodeInput && (
        <div className={`p-3 rounded-lg border ${
          verificationStatus === 'verified' 
            ? 'bg-green-50 border-green-200' 
            : verificationStatus === 'error'
            ? 'bg-red-50 border-red-200'
            : verificationStatus === 'warning'
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {verificationStatus === 'verified' && <CheckCircle className="w-5 h-5 text-green-600" />}
              {verificationStatus === 'error' && <XCircle className="w-5 h-5 text-red-600" />}
              {verificationStatus === 'warning' && <AlertCircle className="w-5 h-5 text-yellow-600" />}
            </div>
            <div className="ml-3 flex-1">
              <p className={`text-sm font-medium ${
                verificationStatus === 'verified' 
                  ? 'text-green-800' 
                  : verificationStatus === 'error'
                  ? 'text-red-800'
                  : 'text-yellow-800'
              }`}>
                {verificationDetails.message}
              </p>
              
              {verificationDetails.suggestion && (
                <button
                  type="button"
                  onClick={() => {
                    const newEmail = `${email.split('@')[0]}@${verificationDetails.suggestion}`;
                    onChange({ target: { name: 'email', value: newEmail } });
                    setVerificationStatus(null);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                >
                  Use suggested email
                </button>
              )}

              {verificationDetails.canRetry && (
                <button
                  type="button"
                  onClick={handleSendVerification}
                  disabled={verifying}
                  className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 flex items-center"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCodeInput && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 mb-3">
            Enter the 6-digit code sent to {email}
          </p>
          <div className="flex space-x-2">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setVerificationCode(value);
                setVerificationDetails(prev => ({ ...prev, codeError: null }));
              }}
              placeholder="000000"
              maxLength="6"
              className="flex-1 px-4 py-2 text-center text-lg font-mono border rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={verifying || verificationCode.length !== 6}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                verifying || verificationCode.length !== 6
                  ? 'bg-[#1C3348] text-slate-500 cursor-not-allowed'
                  : 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
              }`}
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Verify'
              )}
            </button>
          </div>
          
          {verificationDetails?.codeError && (
            <p className="text-red-600 text-sm mt-2">
              {verificationDetails.codeError}
            </p>
          )}

          <div className="mt-3 text-sm text-gray-600">
            Didn't receive the code?{' '}
            {resendTimer > 0 ? (
              <span>Resend in {resendTimer}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// OAuth Button Component - Google Only
const OAuthButton = ({ onClick, loading, disabled, darkMode }) => {
  return (
    <button
      type="button"
      onClick={() => onClick('google')}
      disabled={disabled || loading === 'google'}
      className={`w-full flex items-center justify-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 border ${
        darkMode
          ? 'bg-[#13283C] hover:bg-[#1C3348] text-white border-[#28445F]'
          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {loading === 'google' ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )}
      <span>Continue with Google</span>
    </button>
  );
};

// Helper function to get the correct icon for a field
const getFieldIcon = (fieldName) => {
  const icons = {
    username: <User className="w-5 h-5" />,
    email: <Mail className="w-5 h-5" />,
    password: <Lock className="w-5 h-5" />,
    confirmPassword: <Lock className="w-5 h-5" />,
    first_name: <User className="w-5 h-5" />,
    last_name: <User className="w-5 h-5" />,
    company: <Building className="w-5 h-5" />,
    position: <Briefcase className="w-5 h-5" />,
    phone: <Phone className="w-5 h-5" />,
    registration_reason: <MessageSquare className="w-5 h-5" />
  };
  return icons[fieldName] || <User className="w-5 h-5" />;
};

// InputField component
const InputField = React.memo(({ 
  icon,
  name,
  type = "text",
  placeholder,
  value,
  onChange,
  error,
  loading,
  disabled,
  darkMode
}) => {
  const hasError = error;
  const isValid = value && !hasError;
  const IconComponent = icon || (() => getFieldIcon(name));

  return (
    <div className="space-y-1">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
          <IconComponent />
        </div>
        <input
          type={type}
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={loading || disabled}
          className={`w-full px-4 py-3 pl-12 border rounded-lg transition-colors duration-200 ${
            hasError
              ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
              : isValid
              ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200'
              : darkMode
              ? 'border-[#28445F] bg-[#13283C] text-white focus:border-blue-500 focus:ring-blue-200'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
          } focus:outline-none focus:ring-2`}
        />
        {hasError && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
        )}
        {isValid && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
        )}
      </div>
      {hasError && (
        <p className="text-red-600 text-sm mt-1 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {hasError}
        </p>
      )}
    </div>
  );
});

// PasswordField component
const PasswordField = React.memo(({ 
  name,
  placeholder,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  error,
  loading,
  darkMode
}) => {
  const hasError = error;
  const isValid = value && !hasError;

  return (
    <div className="space-y-1">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
          <Lock className="w-5 h-5" />
        </div>
        <input
          type={showPassword ? "text" : "password"}
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={loading}
          className={`w-full px-4 py-3 pl-12 pr-12 border rounded-lg transition-colors duration-200 ${
            hasError
              ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
              : isValid
              ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200'
              : darkMode
              ? 'border-[#28445F] bg-[#13283C] text-white focus:border-blue-500 focus:ring-blue-200'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
          } focus:outline-none focus:ring-2`}
        />
        <button
          type="button"
          onClick={onTogglePassword}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      {hasError && (
        <p className="text-red-600 text-sm mt-1 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {hasError}
        </p>
      )}
    </div>
  );
});

// Registration Success Component
const RegistrationSuccess = ({ formData, subscriptionInfo, onNavigate, darkMode }) => (
  <div className="w-full max-w-md mx-auto">
    <div className={`${darkMode ? 'bg-[#0D1E2E] border border-[#1C3348]' : 'bg-white'} rounded-md p-8 text-center`}>
      <div className="mb-6">
        <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Registration Successful!
        </h2>
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
          Welcome to TunaSignal AI, {formData.first_name}!
        </p>
      </div>

      <div className={`border ${darkMode ? 'border-[#1C3348]' : 'border-gray-200'} rounded-lg p-4 mb-6 bg-slate-50 dark:bg-[#13283C]`}>
        <div className="flex items-center mb-2">
          <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {subscriptionInfo?.awaiting_payment ? 'Complete Payment' : 'Account Status'}
          </h3>
        </div>
        
        {subscriptionInfo?.awaiting_payment ? (
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
            Your account has been created! Please complete your payment to activate your subscription.
          </p>
        ) : subscriptionInfo?.plan_name ? (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center mb-1">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
              <span className="text-sm font-medium text-green-800 dark:text-green-300">
                {subscriptionInfo.plan_name} Activated!
              </span>
            </div>
            <p className="text-xs text-green-700 dark:text-green-400">
              Your subscription is ready to use.
            </p>
          </div>
        ) : (
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
            Your account is pending admin approval. You'll receive an email once activated.
          </p>
        )}
      </div>

      <div className={`space-y-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        <p>📧 Email: <strong>{formData.email}</strong></p>
        <p>👤 Username: <strong>{formData.username}</strong></p>
        {subscriptionInfo && subscriptionInfo.plan_name && (
          <p>🎁 Plan: <strong>{subscriptionInfo.plan_name}</strong></p>
        )}
      </div>

      <button
        onClick={onNavigate}
        className="mt-6 w-full bg-[#5AA9F0] text-[#07131F] py-3 px-4 rounded-lg font-medium hover:bg-[#8CC4F5] transition-colors flex items-center justify-center"
      >
        Continue
        <ArrowRight className="w-5 h-5 ml-2" />
      </button>
    </div>
  </div>
);

const RegisterForm = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    company: '',
    position: '',
    phone: '',
    registration_reason: '',
    google_id: '',
    picture: '',
    email_verified: false,
    isGoogleAuth: false
  });

  const [validation, setValidation] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [success, setSuccess] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [emailVerified, setEmailVerified] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  // Validation function
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'username':
        if (!value) errors.username = 'Username is required';
        else if (value.length < 3) errors.username = 'Username must be at least 3 characters';
        else if (!/^[a-zA-Z0-9_]+$/.test(value)) errors.username = 'Username can only contain letters, numbers, and underscores';
        break;

      case 'email':
        if (!value) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errors.email = 'Please enter a valid email address';
        else if (!formData.isGoogleAuth && !emailVerified) errors.email = 'Please verify your email address';
        break;

      case 'password':
        if (!value) errors.password = 'Password is required';
        else if (value.length < 8) errors.password = 'Password must be at least 8 characters';
        else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) errors.password = 'Password must contain uppercase, lowercase, and number';
        break;

      case 'confirmPassword':
        if (!value) errors.confirmPassword = 'Please confirm your password';
        else if (value !== formData.password) errors.confirmPassword = 'Passwords do not match';
        break;  

      case 'first_name':
        if (!value) errors.first_name = 'First name is required';
        else if (value.length < 2) errors.first_name = 'First name must be at least 2 characters';
        break;

      case 'last_name':
        if (!value) errors.last_name = 'Last name is required';
        else if (value.length < 1) errors.last_name = 'Last name must be at least 1 character';
        break;

      case 'phone':
        if (value && !/^[\d\s\-()]+$/.test(value)) {
          errors.phone = 'Please enter a valid phone number';
        }
        break;

      case 'registration_reason':
        if (!value) errors.registration_reason = 'Please tell us why you want to join';
        else if (value.trim().length < 10) errors.registration_reason = 'Please provide more details (at least 10 characters)';
        break;

      default:
        break;
    }

    return errors;
  };

  // Complete registration after subscription selection
  const completeRegistration = useCallback(async (userData, subscriptionData) => {
    try {
      setLoading(true);
      setError('');
  
      console.log('=== COMPLETE REGISTRATION ===');
      console.log('User Data:', { ...userData, password: userData.password ? '[HIDDEN]' : undefined });
      console.log('Subscription Data:', subscriptionData);
  
      // ✅ Validate required fields
      const requiredFields = ['username', 'email', 'first_name', 'last_name'];
      if (!userData.isGoogleAuth) {
        requiredFields.push('password');
      }
      
      const missingFields = requiredFields.filter(field => !userData[field] || !userData[field].trim?.());
      
      if (missingFields.length > 0) {
        console.error('❌ Missing required fields:', missingFields);
        throw new Error(`Missing required fields: ${missingFields.join(', ')}. Please go back and complete the form.`);
      }
  
      // Build submission data
      const submissionData = {
        username: userData.username.trim(),
        email: userData.email.trim().toLowerCase(),
        first_name: userData.first_name.trim(),
        last_name: userData.last_name.trim(),
        registration_reason: userData.registration_reason?.trim() || '',
        
        // Optional fields
        company: userData.company?.trim() || '',
        position: userData.position?.trim() || '',
        phone: userData.phone?.trim() || '',
        
        // Password (only if not Google auth)
        ...(userData.isGoogleAuth ? {} : {
          password: userData.password
        }),
        
        // Google OAuth fields
        google_id: userData.google_id || null,
        picture: userData.picture || null,
        email_verified: userData.isGoogleAuth ? userData.email_verified : emailVerified
      };
  
      // Add subscription data if provided
      if (subscriptionData) {
        submissionData.subscription = {
          type: subscriptionData.type,
          plan_id: subscriptionData.plan_id,
          plan_name: subscriptionData.plan_name
        };
        
        // Set default_subscription based on type
        if (subscriptionData.type === 'trial') {
          submissionData.default_subscription = 'trial';
        } else if (subscriptionData.type === 'paid' || subscriptionData.plan_id === 'monthly') {
          submissionData.default_subscription = 'paid';
        }
      }
  
      console.log('✅ Final submission data:', {
        ...submissionData,
        password: submissionData.password ? '[HIDDEN]' : undefined
      });
  
      let result;
      if (userData.isGoogleAuth) {
        const response = await fetch('/api/auth/google/complete-registration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData)
        });
        
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Registration failed');
        }
        result = data;
      } else {
        result = await register(submissionData);
      }
  
      console.log('✅ Registration result:', result);
      
      // Clear session storage
      sessionStorage.removeItem('registrationData');
      sessionStorage.removeItem('emailVerified');
      sessionStorage.removeItem('subscriptionCompleted');
      sessionStorage.removeItem('subscriptionSkipped');
      sessionStorage.removeItem('subscriptionData');
      
      // Set subscription info for display
      if (result.subscription) {
        setSubscriptionInfo(result.subscription);
      } else if (result.awaiting_payment || result.awaiting_subscription) {
        setSubscriptionInfo({
          awaiting_payment: true,
          subscription_type: result.subscription_type
        });
      }
      
      setRegistrationComplete(true);
      setSuccess(true);
      
      // Navigate based on result
      setTimeout(() => {
        if (result.subscription?.plan_name === 'Trial') {
          navigate('/login', {
            state: {
              message: 'Registration successful! Please log in to start your free trial.',
              email: userData.email
            }
          });
        } else if (result.awaiting_payment) {
          navigate('/pending-approval');
        } else {
          navigate('/pending-approval');
        }
      }, 3000);
      
    } catch (err) {
      console.error('❌ Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  }, [register, navigate, emailVerified]);

  // Check if coming back from subscription page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const step = urlParams.get('step');
    
    if (step === 'complete') {
      const subscriptionCompleted = sessionStorage.getItem('subscriptionCompleted');
      const subscriptionSkipped = sessionStorage.getItem('subscriptionSkipped');
      const registrationData = sessionStorage.getItem('registrationData');
      const wasEmailVerified = sessionStorage.getItem('emailVerified');
      const subscriptionData = sessionStorage.getItem('subscriptionData');
      
      if (registrationData) {
        const userData = JSON.parse(registrationData);
        setFormData(userData);
        setEmailVerified(wasEmailVerified === 'true');
        
        if (subscriptionCompleted === 'true' && subscriptionData) {
          // User selected a subscription
          const subData = JSON.parse(subscriptionData);
          completeRegistration(userData, subData);
        } else if (subscriptionSkipped === 'true') {
          // User skipped subscription
          completeRegistration(userData, null);
        }
      }
    }
  }, [completeRegistration]);

  // Handle OAuth errors and pre-filled data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const message = urlParams.get('message');
    const oauth = urlParams.get('oauth');
    const prefill = urlParams.get('prefill');
    
    if (error) {
      setError(message || `Authentication error: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (oauth === 'google' && prefill === 'true') {
      fetch('/api/auth/google/session-data')
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data) {
            const googleData = data.data;
            
            setFormData(prev => ({
              ...prev,
              email: googleData.email || '',
              first_name: googleData.first_name || '',
              last_name: googleData.last_name || '',
              username: '',
              google_id: googleData.google_id,
              picture: googleData.picture,
              email_verified: googleData.email_verified,
              isGoogleAuth: true,
              password: '',
              confirmPassword: ''
            }));
            
            if (googleData.email_verified) {
              setEmailVerified(true);
            }
            
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        })
        .catch(err => {
          console.error('Error fetching Google data:', err);
        });
    }
  }, []);

  // Handle email verification completion
  const handleEmailVerificationComplete = (verified, email) => {
    setEmailVerified(verified);
    setFormData(prev => ({
      ...prev,
      email_verified: verified
    }));
  };

  // Handle form changes with validation
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newFormData = { ...prev, [name]: value };
      
      if (name === 'email' && !prev.isGoogleAuth) {
        setEmailVerified(false);
      }
      
      const fieldErrors = validateField(name, value);

      setValidation(currentValidation => {
        const newValidation = { ...currentValidation };
        if (Object.keys(fieldErrors).length > 0) {
          newValidation[name] = fieldErrors[name];
        } else {
          delete newValidation[name];
        }
        return newValidation;
      });
      
      return newFormData;
    });
  }, [formData.password]);

  // Step validation
  const validateStep = (step) => {
    let stepErrors = {};

    if (step === 1) {
      const requiredFields = formData.isGoogleAuth 
        ? ['username', 'email', 'first_name', 'last_name']
        : ['username', 'email', 'password', 'confirmPassword', 'first_name', 'last_name'];
        
      requiredFields.forEach(field => {
        const errors = validateField(field, formData[field]);
        stepErrors = { ...stepErrors, ...errors };
      });
      
      if (!formData.isGoogleAuth && formData.email && !emailVerified) {
        stepErrors.email = 'Please verify your email address before proceeding';
      }
    } else if (step === 2) {
      const errors = validateField('registration_reason', formData.registration_reason);
      stepErrors = { ...stepErrors, ...errors };
    }

    setValidation(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  // Navigation functions
  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle OAuth authentication
  const handleOAuthAuth = async (provider) => {
    setError('');
    setOauthLoading(provider);
    
    try {
      const response = await fetch(`/api/auth/${provider}/login`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        throw new Error(data.error || `Failed to initiate ${provider} authentication`);
      }
    } catch (err) {
      console.error(`${provider} auth error:`, err);
      setError(err.message || `${provider} authentication failed. Please try again.`);
    } finally {
      setOauthLoading(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.isGoogleAuth && !emailVerified) {
      setError('Please verify your email address before registering');
      return;
    }
    
    if (!validateStep(2)) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // ✅ CRITICAL FIX: Clean and prepare registration data with only required fields
      const cleanedRegistrationData = {
        // Required fields
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        
        // Password (only if not Google auth)
        ...(formData.isGoogleAuth ? {} : {
          password: formData.password
        }),
        
        // Optional fields (only include if they have values)
        ...(formData.company && formData.company.trim() ? { company: formData.company.trim() } : {}),
        ...(formData.position && formData.position.trim() ? { position: formData.position.trim() } : {}),
        ...(formData.phone && formData.phone.trim() ? { phone: formData.phone.trim() } : {}),
        
        // Registration reason
        registration_reason: formData.registration_reason.trim(),
        
        // Google OAuth fields (if applicable)
        ...(formData.isGoogleAuth ? {
          google_id: formData.google_id,
          picture: formData.picture,
          email_verified: formData.email_verified,
          isGoogleAuth: true
        } : {})
      };
      
      // Validate cleaned data has all required fields
      const requiredFields = ['username', 'email', 'first_name', 'last_name', 'registration_reason'];
      if (!formData.isGoogleAuth) {
        requiredFields.push('password');
      }
      
      const missingFields = requiredFields.filter(field => !cleanedRegistrationData[field]);
      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }
      
      console.log('✅ Cleaned registration data:', {
        ...cleanedRegistrationData,
        password: cleanedRegistrationData.password ? '[HIDDEN]' : undefined
      });
      
      // Store registration data in session
      sessionStorage.setItem('registrationData', JSON.stringify(cleanedRegistrationData));
      sessionStorage.setItem('emailVerified', emailVerified ? 'true' : 'false');
      
      // Navigate to subscription page
      console.log('Navigating to subscription page...');
      navigate('/subscription?from=registration');
      
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Failed to proceed. Please try again.');
      setLoading(false);
    }
  };

  // Handle navigation from success screen
  const handleSuccessNavigation = () => {
    if (subscriptionInfo?.awaiting_payment) {
      // User needs to complete payment
      navigate('/subscription');
    } else {
      navigate('/pending-approval');
    }
  };

  // Show registration success screen if completed
  if (registrationComplete || success) {
    return (
      <div className="dark ts-page">
      <SiteStyles />
      <div className={`min-h-screen ${darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'} flex items-center justify-center p-4`}>
        <RegistrationSuccess 
          formData={formData} 
          subscriptionInfo={subscriptionInfo} 
          onNavigate={handleSuccessNavigation}
          darkMode={darkMode}
        />
      </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto">
        <RegistrationSuccess 
          formData={formData} 
          subscriptionInfo={subscriptionInfo} 
          onNavigate={handleSuccessNavigation}
          darkMode={darkMode}
        />
      </div>
    );
  }

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'} flex items-center justify-center p-4`}>
      <div className="w-full max-w-2xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <img 
                src={tunaLogo} 
                alt="TunaSignal AI" 
                className="h-14 relative z-10"
              />
            </div>
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Create Your Account
          </h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Join TunaSignal AI and start receiving intelligent trading signals
          </p>
        </div>

        {/* Main Card Container */}
        <div className={`${darkMode ? 'bg-[#0D1E2E] border border-[#1C3348]' : 'bg-white'} rounded-md overflow-hidden`}>
          {/* Progress Header */}
          <div className="bg-[#0D1E2E] border-b border-[#1C3348] px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              {[1, 2].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    step <= currentStep
                      ? 'bg-white text-blue-600 transform scale-110'
                      : 'bg-blue-500 bg-opacity-30 text-white'
                  }`}>
                    {step <= currentStep ? (
                      step < currentStep ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        step
                      )
                    ) : (
                      step
                    )}
                  </div>
                  {step < 2 && (
                    <div className={`flex-1 h-1 mx-4 rounded-full transition-all duration-300 ${
                      step < currentStep ? 'bg-white' : 'bg-blue-500 bg-opacity-30'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm text-white">
              <span className={`font-medium transition-opacity ${currentStep >= 1 ? 'opacity-100' : 'opacity-60'}`}>
                Personal Information
              </span>
              <span className={`font-medium transition-opacity ${currentStep >= 2 ? 'opacity-100' : 'opacity-60'}`}>
                Professional Details
              </span>
            </div>
          </div>

          <div className="p-8">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg animate-shake">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                  <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-fadeIn">
                  {/* Section Header */}
                  <div className="pb-3 border-b border-gray-200 dark:border-[#1C3348]">
                    <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <User className="w-5 h-5 mr-2 text-blue-600" />
                      Personal Information
                    </h3>
                  </div>
                  
                  {/* Google Auth Success Message */}
                  {formData.isGoogleAuth && (
                    <div className="mb-6 p-4 bg-slate-50 dark:bg-[#13283C] border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-green-800 dark:text-green-200 font-medium">Google authentication successful!</p>
                          <p className="text-green-700 dark:text-green-300 text-sm mt-1">Please choose a username to complete your registration.</p>
                          <p className="text-green-600 dark:text-green-400 text-xs mt-2">No password required - you'll sign in with Google.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* OAuth Options */}
                  {!formData.isGoogleAuth && (
                    <>
                      <div className="mb-6">
                        <OAuthButton 
                          onClick={handleOAuthAuth}
                          loading={oauthLoading}
                          disabled={loading}
                          darkMode={darkMode}
                        />
                      </div>

                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className={`w-full border-t ${darkMode ? 'border-[#1C3348]' : 'border-gray-300'}`}></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className={`px-4 ${darkMode ? 'bg-[#0D1E2E] text-gray-400' : 'bg-white text-gray-500'}`}>
                            Or register with email
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Form Fields Grid */}
                  <div className="space-y-4">
                    {/* Username */}
                    <InputField
                      icon={User}
                      name="username"
                      placeholder="Choose a username"
                      value={formData.username}
                      onChange={handleChange}
                      error={validation.username}
                      loading={loading}
                      darkMode={darkMode}
                    />

                    {/* Email with Verification */}
                    <EmailVerificationField
                      email={formData.email}
                      onChange={handleChange}
                      error={validation.email}
                      loading={loading}
                      disabled={false}
                      isGoogleAuth={formData.isGoogleAuth}
                      onVerificationComplete={handleEmailVerificationComplete}
                      darkMode={darkMode}
                    />

                    {/* Password Fields - Only for non-Google auth */}
                    {!formData.isGoogleAuth && (
                      <>
                        <PasswordField
                          name="password"
                          placeholder="Create a strong password"
                          value={formData.password}
                          onChange={handleChange}
                          showPassword={showPassword}
                          onTogglePassword={() => setShowPassword(!showPassword)}
                          error={validation.password}
                          loading={loading}
                          darkMode={darkMode}
                        />

                        <PasswordField
                          name="confirmPassword"
                          placeholder="Confirm your password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          showPassword={showConfirmPassword}
                          onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                          error={validation.confirmPassword}
                          loading={loading}
                          darkMode={darkMode}
                        />
                      </>
                    )}

                    {/* Name Fields in Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        icon={User}
                        name="first_name"
                        placeholder="First name"
                        value={formData.first_name}
                        onChange={handleChange}
                        error={validation.first_name}
                        loading={loading}
                        disabled={formData.isGoogleAuth}
                        darkMode={darkMode}
                      />

                      <InputField
                        icon={User}
                        name="last_name"
                        placeholder="Last name"
                        value={formData.last_name}
                        onChange={handleChange}
                        error={validation.last_name}
                        loading={loading}
                        disabled={false}
                        darkMode={darkMode}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={loading}
                    className="w-full bg-[#5AA9F0] text-[#07131F] font-semibold py-3 px-4 rounded-lg font-medium hover:bg-[#8CC4F5] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    Next Step
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </button>
                </div>
              )}

              {/* Step 2: Professional Details */}
              {currentStep === 2 && (
                <div className="space-y-4 animate-fadeIn">
                  {/* Section Header */}
                  <div className="pb-3 border-b border-gray-200 dark:border-[#1C3348]">
                    <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <Briefcase className="w-5 h-5 mr-2 text-slate-400" />
                      Professional Details
                    </h3>
                  </div>
                  
                  {/* Professional Fields */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InputField
                        icon={Building}
                        name="company"
                        placeholder="Company name (optional)"
                        value={formData.company}
                        onChange={handleChange}
                        error={validation.company}
                        loading={loading}
                        darkMode={darkMode}
                      />

                      <InputField
                        icon={Briefcase}
                        name="position"
                        placeholder="Job title (optional)"
                        value={formData.position}
                        onChange={handleChange}
                        error={validation.position}
                        loading={loading}
                        darkMode={darkMode}
                      />
                    </div>

                    <InputField
                      icon={Phone}
                      name="phone"
                      placeholder="Phone number (optional)"
                      value={formData.phone}
                      onChange={handleChange}
                      error={validation.phone}
                      loading={loading}
                      darkMode={darkMode}
                    />

                    {/* Registration Reason Textarea */}
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-start pt-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                      </div>
                      <textarea
                        name="registration_reason"
                        value={formData.registration_reason}
                        onChange={handleChange}
                        placeholder="Why do you want to join TunaSignal AI? (Tell us about your trading experience, goals, etc.)"
                        disabled={loading}
                        rows={4}
                        className={`w-full px-4 py-3 pl-12 border rounded-lg transition-colors duration-200 ${
                          validation.registration_reason
                            ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
                            : darkMode
                            ? 'border-[#28445F] bg-[#13283C] text-white focus:border-blue-500 focus:ring-blue-200'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                        } focus:outline-none focus:ring-2 resize-none`}
                      />
                      {validation.registration_reason && (
                        <p className="text-red-600 text-sm mt-1 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {validation.registration_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Features Preview Card */}
                  <div className={`p-4 bg-slate-50 dark:bg-[#13283C] rounded-lg border ${
                    darkMode ? 'border-[#28445F]' : 'border-blue-200'
                  }`}>
                    <h4 className={`text-sm font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <Star className="w-4 h-4 text-yellow-500 mr-2" />
                      What you'll get with TunaSignal AI:
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[
                        'AI signals',
                        'MACD crossover alerts',
                        'Volume spike detection',
                        'Price notifications',
                        'Unlimited monitoring',
                        'Data export features',
                        'Priority support',
                        'Advanced analytics'
                      ].map((feature, index) => (
                        <div key={index} className="flex items-center">
                          <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 flex-shrink-0" />
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      disabled={loading}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                        darkMode
                          ? 'bg-[#13283C] text-gray-300 hover:bg-[#1C3348]'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" />
                      Previous
                    </button>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#5AA9F0] text-[#07131F] font-semibold py-3 px-4 rounded-lg font-medium hover:bg-[#8CC4F5] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Continue to Subscription
                          <CreditCard className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in
            </Link>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            By registering, you agree to our{' '}
            <Link to="/legal" className="text-blue-600 hover:underline">
              Terms & Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default RegisterForm;